import { cva } from 'class-variance-authority';
import { clsx } from 'clsx';
import { type HTMLAttributes, type ReactNode } from 'react';

import { type AllowedDisplayElements } from '@repo/ui/utils/html';
import {
  type GapUtilityClass,
  type MarginUtilityClass,
  type PaddingUtilityClass,
} from '@repo/ui/utils/spacing';

import s from './Flex.module.css';

export const flexDisplayPropertyOptions = ['flex', 'inline-flex'] as const;
export type FlexDisplayProperties = (typeof flexDisplayPropertyOptions)[number];

type FlexHTMLAttributes<T extends AllowedDisplayElements> = HTMLAttributes<
  Extract<HTMLElementTagNameMap[T], HTMLElement>
>;

export type Direction = 'row' | 'row-reverse' | 'column' | 'column-reverse';

export interface FlexProps<
  T extends AllowedDisplayElements = AllowedDisplayElements,
> extends FlexHTMLAttributes<T> {
  alignItems?: 'center' | 'flex-start' | 'flex-end' | 'baseline' | 'stretch';
  as?: T;
  children: ReactNode;
  className?: string;
  direction?: Direction;
  display?: FlexDisplayProperties;
  gap?: GapUtilityClass | GapUtilityClass[];
  justifyContent?:
    | 'center'
    | 'flex-start'
    | 'flex-end'
    | 'space-between'
    | 'space-around'
    | 'space-evenly';
  margin?: MarginUtilityClass | MarginUtilityClass[];
  padding?: PaddingUtilityClass | PaddingUtilityClass[];
  wrap?: 'wrap' | 'nowrap' | 'wrap-reverse';
  grow?: '0' | '1';
  shrink?: '0' | '1';
}

export const flexVariants = cva(s.flex, {
  variants: {
    alignItems: {
      center: s.alignItemsCenter,
      ['flex-start']: s.alignItemsFlexStart,
      ['flex-end']: s.alignItemsFlexEnd,
      baseline: s.alignItemsBaseline,
      stretch: s.alignItemsStretch,
    },
    display: {
      flex: s.displayFlex,
      ['inline-flex']: s.displayInlineFlex,
    },
    direction: {
      row: s.row,
      ['row-reverse']: s.rowReverse,
      column: s.column,
      ['column-reverse']: s.columnReverse,
    },
    justifyContent: {
      center: s.justifyContentCenter,
      ['flex-start']: s.justifyContentFlexStart,
      ['flex-end']: s.justifyContentFlexEnd,
      ['space-between']: s.justifyContentSpaceBetween,
      ['space-around']: s.justifyContentSpaceAround,
      ['space-evenly']: s.justifyContentSpaceEvenly,
    },
    wrap: {
      wrap: s.wrap,
      nowrap: s.nowrap,
      ['wrap-reverse']: s.wrapReverse,
    },
    grow: {
      ['0']: s.grow0,
      ['1']: s.grow1,
    },
    shrink: {
      ['0']: s.shrink0,
      ['1']: s.shrink1,
    },
    // the gap, margin, and padding variants are not implemented as flexVariants, as they use utility classes and not CSS modules.
  },
  defaultVariants: {},
});

export const Flex = (props: FlexProps) => {
  const {
    alignItems,
    as = 'div',
    children,
    className,
    direction,
    display = 'flex',
    gap,
    justifyContent,
    margin,
    padding,
    wrap,
    grow,
    shrink,
    ...attrs
  } = props;

  if (!children) {
    return null;
  }

  const Component = as;

  return (
    <Component
      className={clsx(
        flexVariants({
          alignItems,
          className,
          direction,
          display,
          justifyContent,
          wrap,
          grow,
          shrink,
        }),
        gap,
        margin,
        padding,
      )}
      {...attrs}
    >
      {children}
    </Component>
  );
};

Flex.displayName = 'Flex';
